import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { tap } from 'rxjs/operators';
import { ToastService } from '../../../../../main/services';
import { Tender } from '../../../../models';
import { TenderContractsService } from '../../../../services';
@Component({
    selector: 'jft-contracts-import-modal',
    templateUrl: './contracts-import-modal.component.html',
    styleUrls: ['./contracts-import-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContractsImportModalComponent implements OnInit {

    public loading: boolean = false;
    public loadingTemplate: boolean = false;
    public file;

    // Results & errors
    public showResults: boolean = false;
    public showValidationResults: boolean = false;
    public importStats: any = null;
    public validationStats: any = null;
    public errors: string[] = [];
    public validationErrors: string[] = [];
    public isImportedCollapsed: boolean = true;
    public isSkippedCollapsed: boolean = true;
    public selectedFile: File | null = null;
    public isProcessBackendTask = false;

    constructor(
        private tender: Tender,
        private contractService: TenderContractsService,
        private toast: ToastService, // kept for template download failures only
        private activeModal: NgbActiveModal,
        private route: ActivatedRoute,
    ) {
    }

    public ngOnInit() {
        let data = this.route.snapshot.data;
    }

    public close() {
        if (this.showResults && (!this.errors || this.errors.length === 0)) {
            this.activeModal.close('Import completed');
        } else {
            this.activeModal.dismiss('close');
        }
    }

    public filesSelected(files: File[]): void {
        if (!files.length) {
            return;
        }

        this.selectedFile = files[0];
        this.validateFile();
    }

    private validateFile(): void {
        if (!this.selectedFile) {
            return;
        }

        this.loading = true;
        this.contractService.validateContracts(this.tender, this.selectedFile)
            .pipe(tap(() => this.loading = false))
            .subscribe({
                next: (data: any) => {
                    this.validationStats = data?.validation_stats || {
                        total_locations_found: 0,
                        locations_with_errors: [],
                        locations_valid: [],
                        validation_errors: []
                    };
                    this.validationErrors = this.validationStats.validation_errors || [];
                    this.showValidationResults = true;
                },
                error: (err) => {
                    this.loading = false;
                    const response = err?.error;
                    this.validationStats = response?.validation_stats || {
                         total_locations_found: 0,
                         locations_with_errors: [],
                         locations_valid: [],
                         validation_errors: []
                    };

                    if (this.validationStats.validation_errors && this.validationStats.validation_errors.length > 0) {
                        this.validationErrors = this.validationStats.validation_errors;
                    } else {
                        const details = response?.details ?? response ?? 'Validation failed';
                        this.validationErrors = Array.isArray(details) ? details : [details];
                        // Ensure validation stats has the errors for consistency
                        this.validationStats.validation_errors = this.validationErrors;
                    }
                    this.showValidationResults = true;
                }
            });
    }

    public proceedWithImport(): void {
        if (!this.selectedFile) {
            return;
        }

        this.loading = true;
        this.showValidationResults = false;
        this.contractService.uploadContracts(this.tender, this.selectedFile)
            .pipe(tap(() => this.loading = false))
            .subscribe({
                next: (data: any) => {
                    // Expecting optional import_stats and optionally details (array of errors)
                    this.isProcessBackendTask = data?.type == 'task';
                    this.importStats = data?.import_stats || {
                        total_locations_found: null,
                        locations_imported: [],
                        locations_skipped: []
                    };
                    const details = data?.details;
                    this.errors = Array.isArray(details) ? details : [];
                    this.showResults = true;
                },
                error: (err) => {
                    this.loading = false;
                    // Do not close modal; show errors inline
                    const details = err?.error?.details ?? err?.error ?? 'Import failed';
                    this.errors = Array.isArray(details) ? details : [details];
                    this.showResults = true;
                }
            });
    }

    public cancelImport(): void {
        this.showValidationResults = false;
        this.selectedFile = null;
        this.validationStats = null;
        this.validationErrors = [];
    }

    public getValidSkippedLocations(skippedLocations: any[]): any[] {
        if (!skippedLocations) return [];
        return skippedLocations.filter(location => location.validation_passed);
    }

    public getErrorSkippedLocations(skippedLocations: any[]): any[] {
        if (!skippedLocations) return [];
        return skippedLocations.filter(location => !location.validation_passed);
    }

    public downloadTemplate($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        if (this.loadingTemplate) {
            return;
        }
        this.loadingTemplate = true;

        this.contractService.downloadLocationsXlsTemplateObservable(this.tender)
            .pipe(tap(() => this.loadingTemplate = false))
            .subscribe({
                next: () => {
                    // FileSaver logic is handled in the service
                },
                error: () => {
                    this.loadingTemplate = false;
                    this.toast.error('Failed to download template');
                }
            });
    }
}
